import type {
  KeyResult,
  PaginatedResponse,
  TerminalConnectionToken,
  User,
  UserExpandable,
  UserKeyParams,
  UserListOptions,
  UserRefreshParams,
  UserResetPasswordParams,
  UserWithToken,
} from '@types'
import { apiRequest, paginatedRequest } from './base'

export const userService = {
  /**
   * @roles authenticated, driver, charity, partner, captain
   */
  async list<E extends UserExpandable = never>(
    options: UserListOptions & { expand?: E | E[] } = {}
  ): Promise<PaginatedResponse<User>> {
    return paginatedRequest('/users', { method: 'GET', params: options })
  },

  /**
   * @roles authenticated, driver, charity, partner, captain
   */
  async get<E extends UserExpandable = never>(
    id: number,
    options: { expand?: E | E[] } = {}
  ): Promise<User> {
    return apiRequest(`/users/${id}`, {
      method: 'GET',
      params: options.expand
        ? {
            expand: Array.isArray(options.expand)
              ? options.expand.join(',')
              : options.expand,
          }
        : {},
    })
  },

  /**
   * @roles charity, partner, captain
   */
  async create(data: Partial<User>): Promise<User> {
    return apiRequest('/users', { method: 'POST', body: data })
  },

  /**
   * @roles authenticated, driver, charity, partner, captain
   */
  async update(id: number, data: Partial<User>): Promise<User> {
    return apiRequest(`/users/${id}`, { method: 'PUT', body: data })
  },

  /**
   * @roles charity, partner, captain
   */
  async delete(id: number): Promise<void> {
    return apiRequest(`/users/${id}`, { method: 'DELETE' })
  },

  /**
   * @roles charity, partner, captain
   */
  async restore(id: number): Promise<User> {
    return apiRequest(`/users/${id}/restore`, { method: 'POST' })
  },

  /**
   * @roles public, charity, partner, captain
   */
  async signUp(data: Partial<User>): Promise<UserWithToken> {
    return apiRequest(`/users/sign_up`, { method: 'POST', body: data })
  },

  /**
   * @roles public, charity, partner, captain
   */
  async signIn(data: Partial<User>): Promise<UserWithToken> {
    return apiRequest(`/users/sign_in`, { method: 'POST', body: data })
  },

  /**
   * @roles authenticated, driver, charity, partner, captain
   */
  async signOut(): Promise<void> {
    return apiRequest(`/users/sign_out`, { method: 'POST' })
  },

  /**
   * @roles public, charity, partner, captain
   */
  async refresh(data: UserRefreshParams = {}): Promise<UserWithToken> {
    return apiRequest(`/users/refresh`, { method: 'POST', body: data })
  },

  /**
   * @roles public, charity, partner, captain
   */
  async resetPassword(data: UserResetPasswordParams = {}): Promise<void> {
    return apiRequest(`/users/reset_password`, { method: 'POST', body: data })
  },

  /**
   * @roles charity, partner, captain
   */
  async key(data: UserKeyParams = {}): Promise<KeyResult> {
    return apiRequest(`/users/key`, { method: 'POST', body: data })
  },

  /**
   * @roles driver, charity, partner, captain
   */
  async terminalConnect(id: string | number): Promise<TerminalConnectionToken> {
    return apiRequest(`/users/${id}/terminal/connect`, { method: 'POST' })
  },
}
